import { Alert, Badge, Button, Card, CardBody, CardFooter, CardHeader, CurrencyDisplay, Icon, Input, InputWithTags, Modal, Select, SelectOptionType, Tabs } from '@aidkitorg/component-library';
import React, { useCallback, useEffect, useState } from 'react';
import { useAccounts } from '../../../Contexts/AccountsContext';
import { accountKindToName } from '../AccountsPage';
import { useLocalizedStrings } from '../../../Localization';
import { Account, AccountBalanceReminder, AccountBalanceReminderConfig } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { generateDateString, onlyEmails } from '../../../utils/adminPanel.utils';

type CreateReminderTabProps = {
  setRemindersModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const CreateReminderTab: React.FC<CreateReminderTabProps> = ({ setRemindersModalOpen }) => {
  const L = useLocalizedStrings()

  const [account, setAccount] = useState<Account['kind']>('unknown')
  const [thresholdAmount, setThresholdAmount] = useState<string>()
  const [recipients, setRecipients] = useState<Array<string>>([])

  const [reminderCreated, setReminderCreated] = useState(false)

  const { 
    accounts, 
    accountBalanceReminderCallInFlight,
    createAccountBalanceReminder 
  } = useAccounts()

  const [createDisabled, setCreateDisabled] = useState(true)

  const ACCOUNTS_VALUES: Array<SelectOptionType> = []
  accounts.forEach((account) => {
    const option = { value: account.kind, label: accountKindToName(account.kind) }
    ACCOUNTS_VALUES.push(option)
  })

  useEffect(() => {
    const disableSubmit = 
        account == 'unknown' ||
        isNaN(Number(thresholdAmount)) || 
        Number(thresholdAmount) < 0 || 
        recipients.length === 0 ||
        recipients.join(',') == '' ||
        accountBalanceReminderCallInFlight

    setCreateDisabled(disableSubmit)
  }, [account, thresholdAmount, recipients, accountBalanceReminderCallInFlight])

  const createReminder = useCallback(async () => {
    const reminderConfig: AccountBalanceReminderConfig = {
      account: account,
      thresholdInCents: Number(thresholdAmount) * 100,
      emails: recipients,
    }

    const result = await createAccountBalanceReminder(reminderConfig)
    if (result) {
      setThresholdAmount("")
      setRecipients([])
      setAccount('unknown')
      setReminderCreated(true)
    }
  }, [account, thresholdAmount, recipients, createAccountBalanceReminder, setRemindersModalOpen])

  return (
    <div className="min-h-[23rem] pb-2 pt-4">
      <div className="flex flex-col gap-y-4">
        <div className="text-gray-700">
          {L.admin_panel.accounts.balance_threshold_desc}
        </div>
        <div>
          <Select 
            options={ACCOUNTS_VALUES}
            description={L.admin_panel.accounts.account_desc}
            label={L.admin_panel.accounts.account}
            labelLocation="above"
            value={ACCOUNTS_VALUES.find((v) => v.value === account)}
            onChange={(value) => setAccount(value.value as Account['kind'])}
          />
        </div>
        <div>
          <Input
            value={thresholdAmount} 
            onChange={(e) => {setThresholdAmount(e.target.value)}} 
            isCurrency={true}
            currencySymbol='$'
            decimalScale={2}
            label={L.admin_panel.accounts.threshold_amt}
          />
        </div>
        <div>
          <InputWithTags 
            validate={onlyEmails}
            errorMessage={L.admin_panel.accounts.email_validation_error_msg}
            label={L.admin_panel.accounts.recipients}
            placeholder={L.admin_panel.accounts.recipients_desc}
            value={recipients}
            onChange={setRecipients}
          />
        </div>
        <div role="alert">
          {reminderCreated && (
            <Alert className="p-0 py-3 px-4" variant='success'>{L.admin_panel.accounts.success_msg}</Alert>
          )}
        </div>
      </div>
      <div className="pt-4 flex justify-end gap-x-2">
        <Button onClick={() => setRemindersModalOpen(false)} variant="secondary">{L.admin_panel.accounts.cancel}</Button>
        <Button isDisabled={createDisabled} onClick={() => createReminder()}>{L.admin_panel.accounts.create_reminder}</Button>
      </div>
    </div>
  )
}

type ReminderCardProps = {
  reminder: AccountBalanceReminder
}

const ReminderCard: React.FC<ReminderCardProps> = ({ reminder }) => {
  const L = useLocalizedStrings()

  const {editAccountBalanceReminder, accountBalanceReminderCallInFlight} = useAccounts()

  const [isInEditMode, setIsInEditMode] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [thresholdAmount, setThresholdAmount] = useState<string>(String(reminder.thresholdInCents * 0.01))
  const [recipients, setRecipients] = useState<Array<string>>([...reminder.emails])

  const cardTitle = (<div className="flex items-center gap-2">
    <Icon className="text-blue-600" strokeWidth={2} name="BellIcon" size="md" role="decorative" />
    <div className="font-medium text-black">{accountKindToName(reminder.account)}</div>
  </div>)

  const cardHeader = (<div className="flex justify-between">
    {cardTitle}
    <div className="flex gap-2">
      <Button
        className="!p-0.5 !cursor-pointer hover:text-blue-600"
        icon={<Icon
          strokeWidth={1.5}
          className="w-1/8"
          name="PencilSquareIcon"
          role="decorative"
        />}
        variant="unstyled"
        size="sm"
        onClick={() => {
          setIsInEditMode(true)
        }}
        aria-label={L.admin_panel.accounts.edit_reminder}
      />
      <Button
        className="!p-0.5 !cursor-pointer hover:text-red-600"
        icon={<Icon
          strokeWidth={1.5}
          className="w-1/8"
          name="TrashIcon"
          role="decorative"
        />}
        variant="unstyled"
        size="sm"
        onClick={() => {
          setDeleteModalOpen(true)
        }}
        aria-label={L.admin_panel.accounts.delete_reminder}
      />
    </div>
  </div>)

  const bullet = '\u2022';
  
  const formattedThreshold = <CurrencyDisplay locale="en-US" currency='USD' value={reminder.thresholdInCents * 0.01}/>

  const cancelEdit = useCallback(() => {
    setThresholdAmount(String(reminder.thresholdInCents * 0.01))
    setRecipients([...reminder.emails])
    setIsInEditMode(false)
  }, [reminder])

  const saveEdits = useCallback(async () => {
    editAccountBalanceReminder({
      reminderKey: reminder.key,
      thresholdAmount: Number(thresholdAmount) * 100,
      recipients: recipients
    })

    setIsInEditMode(false)
  }, [reminder, thresholdAmount, recipients])

  const editModeCardBody = (
    <div className="pt-2 pb-4 flex flex-col gap-y-4">
      <div>
        <Input
          value={thresholdAmount} 
          onChange={(e) => {setThresholdAmount(e.target.value)}} 
          isCurrency={true}
          currencySymbol='$'
          decimalScale={2}
          label={L.admin_panel.accounts.threshold_amt}
        />
      </div>
      <div>
        <InputWithTags 
          validate={onlyEmails}
          errorMessage={L.admin_panel.accounts.email_validation_error_msg}
          label={L.admin_panel.accounts.recipients}
          placeholder={L.admin_panel.accounts.recipients_desc}
          value={recipients}
          onChange={setRecipients}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={cancelEdit}>{L.admin_panel.accounts.cancel}</Button>
        <Button 
          isDisabled={accountBalanceReminderCallInFlight}
          onClick={async () => await saveEdits()}
        >
          {L.admin_panel.accounts.save}
        </Button>
      </div>
    </div>
  )

  const displayCardBody = (
    <div className="flex flex-col gap-2 py-2">
      <div>
        {L.admin_panel.accounts.threshold}: {formattedThreshold}
      </div>
      <div className="flex gap-2">
        {reminder.emails.map((email, idx) => (
          <Badge key={idx} variant="neutral">{email}</Badge>
        ))}
      </div>
    </div>
  )

  const deleteReminder = useCallback(async () => {
    await editAccountBalanceReminder({reminderKey: reminder.key, active: false})
    setDeleteModalOpen(false)
  }, [reminder])

  const deleteModal = (
    <Modal title={L.admin_panel.accounts.delete_reminder_title} open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
      <div>{L.admin_panel.accounts.delete_reminder_confirmation_msg}</div>
      <div className="flex pt-2 justify-end">
        <Button isDisabled={accountBalanceReminderCallInFlight} onClick={async () => await deleteReminder()} variant="danger">{L.admin_panel.accounts.delete}</Button>
      </div>
    </Modal>
  )
  
  return (
    <Card className="rounded-md">
      <CardHeader title={cardHeader} />
      <CardBody className="text-sm">
        {isInEditMode ? editModeCardBody : displayCardBody }
        {deleteModal}
      </CardBody>
      <CardFooter>
        <div className="text-sm text-gray-500">{L.admin_panel.accounts.created_by} {reminder.createdBy} {bullet} {L.admin_panel.accounts.last_modified} {generateDateString(reminder.lastUpdated)}</div>
      </CardFooter>
    </Card>
  )
}

const ActiveRemindersTab = () => {
  const L = useLocalizedStrings()

  const { accountBalanceReminderCallInFlight, accountBalanceReminders } = useAccounts()

  const emptyState = (
    <div className="h-[24rem] flex items-start justify-center h-full text-center text-gray-700 min-h-[20rem] pt-4">
      <span>{L.admin_panel.accounts.active_reminders_empty_state}</span>
    </div>
  )

  const nonEmptyState = (<div tabIndex={0} className="h-[23rem] overflow-auto flex flex-col gap-2 pt-2">
    {
      accountBalanceReminders.length === 0 && !accountBalanceReminderCallInFlight 
        ? emptyState 
        : accountBalanceReminders.map((reminder, idx) => {
          return (<div key={idx}>
            <ReminderCard reminder={reminder} />
          </div>)
        })
    }
  </div>)

  return accountBalanceReminders.length === 0 && !accountBalanceReminderCallInFlight 
    ? emptyState 
    : nonEmptyState
}

type ManageBalanceRemindersModalProps = {
  remindersModalOpen: boolean,
  setRemindersModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const ManageBalanceRemindersModal: React.FC<ManageBalanceRemindersModalProps> = ({
  remindersModalOpen,
  setRemindersModalOpen,
}) => {
  const L = useLocalizedStrings()

  const { accountBalanceReminderCallInFlight, accountBalanceReminders, fetchAccountBalanceReminders } = useAccounts()
    
  useAsyncEffect(async () => {
    if (remindersModalOpen) {
      await fetchAccountBalanceReminders()
    }
  }, [remindersModalOpen])
    
  const tabs = [
    { name: L.admin_panel.accounts.create_reminder, key: 'create_reminder', content: <CreateReminderTab setRemindersModalOpen={setRemindersModalOpen} /> },
    { 
      name: L.admin_panel.accounts.active_reminders, 
      key: 'active_reminders', 
      content: <ActiveRemindersTab />,
      count: accountBalanceReminders.length > 0 ? { value: accountBalanceReminders.length, level: 'info' as const } : undefined
    }
  ]
  return (
    <Modal size="lg" preventBackdropClick={accountBalanceReminderCallInFlight} title={L.admin_panel.accounts.manage_reminders} open={remindersModalOpen} onClose={() => setRemindersModalOpen(false)}>
      <Tabs unmountTabs tabs={tabs} />
    </Modal>
  )
}